import {
  toWSDateString,
  useModalOldContext,
  WSButton,
  WSButtonProps,
  WSElement,
  WSElementColorsType,
  WSFlexBox,
  WSIcon,
  WSIconName,
  WSList,
  WSLoader,
  WSMenuItem,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  Payable,
  PayableStatus,
  PayeeReviewStatus,
  PayerApprovalStatus,
  Payee,
  UpdatePayableWorkflowStatusRequestPayerApprovalStatus
} from "@wingspanhq/wingspan-api-sdk";
import React, { useCallback } from "react";
import { useHistory, useLocation } from "react-router";
import { Actions } from "../../../components/Actions";
import { queriesPayableRows, queriesPayables } from "@query";
import { useSession } from "@hooks/useSession";
import { selectorPayeeDisplayName } from "../../../shared/selectors/selectorPayeeDisplayName";
import {
  useMutationDownloadInvoicePdf,
  useMutationDownloadInvoiceReceiptPdf
} from "../../../query/payments/mutations";
import { QUERY_PAYABLES } from "../../../query/payables/keys";
import { DELETE_PAYABLE_MODAL } from "../DeletePayableModal/DeletePayableModal";
import {
  InvoiceStatusTimeline,
  InvoiceTimelineStatus
} from "../InvoiceStatusTimeline/InvoiceStatusTimeline";
import { REJECT_PAYABLE_MODAL } from "../RejectPayableModal/RejectPayableModal";
import { selectorIsPayableCreator } from "./selectorIsPayableCreator";

const PAYABLE_LIST_DEPENDENCIES = [
  QUERY_PAYABLES,
  queriesPayableRows.searchPayableRows.queryKeyBase
];

// `payee` is the expansion the v3 payable read inlines for a payer caller; it is
// absent when that perspective is not retrievable, so the payee row is optional.
type Props = { payable: Payable; payee?: Payee };

export const PayableDetailsHeader: React.FC<Props> = ({ payable, payee }) => {
  const { accountId } = useSession();

  const [updateWorkflowStatus, updateWorkflowStatusMeta] =
    queriesPayables.updatePayableWorkflowStatus.useMutation({
      dependencies: PAYABLE_LIST_DEPENDENCIES
    });
  const [openPayable, openPayableMeta] =
    queriesPayables.openPayable.useMutation({
      dependencies: PAYABLE_LIST_DEPENDENCIES
    });
  const { openModal } = useModalOldContext();
  const [downloadInvoicePdf] = useMutationDownloadInvoicePdf();
  const [downloadInvoiceReceiptPdf, downloadInvoiceReceiptPdfMeta] =
    useMutationDownloadInvoiceReceiptPdf();

  const history = useHistory();
  const location = useLocation();

  const payableId = payable.id!;

  const setPayerApprovalStatus = useCallback(
    (
      payerApprovalStatus: UpdatePayableWorkflowStatusRequestPayerApprovalStatus
    ) =>
      updateWorkflowStatus({
        payableId,
        updatePayableWorkflowStatusRequest: { payerApprovalStatus }
      }),
    [payableId, updateWorkflowStatus]
  );

  const onEdit = useCallback(() => {
    history.push({
      pathname: `/member/invoices/payables/${payableId}/edit`,
      state: {
        backPath: location.pathname + (location.search || "")
      }
    });
  }, [history, location.pathname, location.search, payableId]);

  return (
    <WSElement>
      {(() => {
        const status = payable.status;
        const payerApproval = payable.payerApprovalStatus;
        // Dual-stage approval came from v1 payroll settings (retired; v3
        // `PayrollSettings.workflow` exists but hangs off a payer-relationship
        // resource, not the acting account). Single-stage is the default, so
        // the pre-approve step is not offered until that read is sourced.
        const isDualStage = false;
        const hasEditAccess = !!accountId && payable.accountId === accountId;

        const isOpen =
          status === PayableStatus.OPENED || status === PayableStatus.PENDING;
        const isOverdue =
          status === PayableStatus.OPENED &&
          !!payable.dueDate &&
          payable.dueDate < new Date();
        const isRejected =
          payerApproval === PayerApprovalStatus.DECLINED &&
          payable.payeeReviewStatus !== PayeeReviewStatus.RESUBMITTED;
        const isApprovalPending =
          isOpen &&
          payerApproval !== PayerApprovalStatus.APPROVED &&
          payerApproval !== PayerApprovalStatus.DECLINED;
        const isApproved =
          isOpen && payerApproval === PayerApprovalStatus.APPROVED;
        // Rejecting is the payer's answer to an invoice the contractor sent, so
        // it is never offered on a payable the payer raised itself — that one is
        // cancelled or removed instead.
        const isPayableCreator = selectorIsPayableCreator({
          payable,
          accountId
        });

        const openNowButtonProps = {
          children: "Open now",
          onClick: () => {
            openPayable({ payableId, ifMatch: "*" });
          },
          loading: openPayableMeta.isLoading
        };

        const preApproveNowButtonProps = {
          children: "Pre-Approve now",
          onClick: () => {
            setPayerApprovalStatus(
              UpdatePayableWorkflowStatusRequestPayerApprovalStatus.PRE_APPROVED
            );
          },
          loading: updateWorkflowStatusMeta.isLoading
        };

        const approveNowButtonProps = {
          children: "Approve now",
          onClick: () => {
            // The funding-method precheck read v1 payroll settings + client;
            // both are retired. Approving proceeds without the client-side
            // warning — the backend still rejects an unfunded approval.
            setPayerApprovalStatus(
              UpdatePayableWorkflowStatusRequestPayerApprovalStatus.APPROVED
            );
          },
          loading: updateWorkflowStatusMeta.isLoading
        };

        const unapproveButtonProps = {
          children: "Unapprove",
          onClick: () => {
            setPayerApprovalStatus(
              isDualStage && payerApproval === PayerApprovalStatus.APPROVED
                ? UpdatePayableWorkflowStatusRequestPayerApprovalStatus.PRE_APPROVED
                : UpdatePayableWorkflowStatusRequestPayerApprovalStatus.PENDING
            );
          },
          loading: updateWorkflowStatusMeta.isLoading
        };

        const downloadPDFButtonProps = {
          children: "Download receipt PDF",
          onClick: () => {
            downloadInvoiceReceiptPdf({ invoiceId: payableId });
          },
          loading: downloadInvoiceReceiptPdfMeta.isLoading
        };

        const editButtonProps = {
          children: "Edit",
          onClick: onEdit
        };

        let icon: WSIconName | undefined;
        let iconColor: WSElementColorsType | undefined;
        let header: string = "";
        let subheader: string = "";
        let timelineStatus: InvoiceTimelineStatus | undefined;
        let buttonProps: WSButtonProps | null = null;

        if (isRejected) {
          header = "Invoice rejected";
          icon = "alert";
          iconColor = "red400";
          subheader = "Invoice was rejected";
          timelineStatus = InvoiceTimelineStatus.Rejected;
        } else if (isApprovalPending) {
          if (isDualStage) {
            if (payerApproval === PayerApprovalStatus.PRE_APPROVED) {
              subheader = "Approve or pay now";
              buttonProps = approveNowButtonProps;
              timelineStatus = InvoiceTimelineStatus.PreApproved;
            } else {
              subheader = "Pre-Approve";
              buttonProps = preApproveNowButtonProps;
              timelineStatus = InvoiceTimelineStatus.Open;
            }
          } else {
            subheader = "Approve or pay now";
            buttonProps = approveNowButtonProps;
            timelineStatus = InvoiceTimelineStatus.Open;
          }

          if (status === PayableStatus.PENDING) {
            header = "Eligibility pending";
            subheader = "Waiting on contractor to enable digital payments";
          } else if (isOverdue) {
            header = "Invoice payment is overdue";
            icon = "info-circle";
            iconColor = "red400";
          } else {
            header = "Invoice open";
          }
        } else if (isApproved) {
          // The exact pay date came from the v1 payroll schedule; v3
          // `PayrollSettings` carries no `scheduleDates` (the schedule lives on
          // the payrolls resource), so this shows the generic wording until
          // that read is sourced.
          header = "Approved for payment";
          subheader = "Will be paid on future payroll";
          buttonProps = unapproveButtonProps;
          timelineStatus = InvoiceTimelineStatus.Approved;
        } else if (status === PayableStatus.PAID) {
          header = "Invoice Paid";
          subheader = payable.events?.markedPaidAt
            ? `Invoice was marked as paid on ${toWSDateString(
                payable.events.markedPaidAt,
                "monthDate"
              )}`
            : payable.events?.paidAt
              ? `Invoice was processed (paid) on ${toWSDateString(
                  payable.events.paidAt,
                  "monthDate"
                )}`
              : "Invoice was paid";
          buttonProps = downloadPDFButtonProps;
          timelineStatus = InvoiceTimelineStatus.Paid;
        } else if (status === PayableStatus.PAYMENT_IN_TRANSIT) {
          header = "Waiting on wire to arrive";
          subheader = "Invoice was marked paid via bank transfer";
        } else if (status === PayableStatus.CREATED) {
          header = "This is a draft";
          subheader = "Open invoice to pay";
          buttonProps =
            (payable.amount ?? 0) > 0 ? openNowButtonProps : editButtonProps;
        }

        const menuItems: WSMenuItem[] = [];

        if (
          payerApproval === PayerApprovalStatus.PRE_APPROVED ||
          payerApproval === PayerApprovalStatus.APPROVED
        ) {
          menuItems.push({
            icon: "block",
            label: "Unapprove",
            onClick: () => {
              setPayerApprovalStatus(
                isDualStage && payerApproval === PayerApprovalStatus.APPROVED
                  ? UpdatePayableWorkflowStatusRequestPayerApprovalStatus.PRE_APPROVED
                  : UpdatePayableWorkflowStatusRequestPayerApprovalStatus.PENDING
              );
            }
          });
        }

        if (
          (status === PayableStatus.CREATED ||
            status === PayableStatus.OPENED ||
            status === PayableStatus.PENDING) &&
          hasEditAccess
        ) {
          menuItems.push({
            icon: "edit",
            label: "Edit",
            onClick: onEdit
          });
        }

        if (isApprovalPending && !isRejected && !isPayableCreator) {
          menuItems.push({
            icon: "exit",
            label: "Reject",
            onClick: () => {
              openModal(REJECT_PAYABLE_MODAL, {
                payee,
                payable
              });
            }
          });
        }

        if (status === PayableStatus.CREATED && hasEditAccess) {
          menuItems.push({
            icon: "exit",
            label: "Remove",
            onClick: () => {
              openModal(DELETE_PAYABLE_MODAL, {
                payable,
                payee
              });
            }
          });
        }

        if (
          (status === PayableStatus.OPENED ||
            status === PayableStatus.PENDING) &&
          hasEditAccess
        ) {
          menuItems.push({
            icon: "exit",
            label: "Cancel invoice",
            onClick: () => {
              openModal(DELETE_PAYABLE_MODAL, {
                payable,
                payee
              });
            }
          });
        }

        // Download PDF
        if (
          status !== PayableStatus.CREATED &&
          status !== PayableStatus.PENDING
        ) {
          menuItems.push({
            icon: "download",
            label: "Download PDF",
            onClick: () => {
              downloadInvoicePdf({ invoiceId: payableId });
            }
          });
        }

        // Download Receipt PDF
        if (status === PayableStatus.PAID) {
          menuItems.push({
            icon: "download",
            label: "Download receipt PDF",
            onClick: () => {
              downloadInvoiceReceiptPdf({ invoiceId: payableId });
            }
          });
        }

        let timelineStatuses = [
          InvoiceTimelineStatus.Open,
          InvoiceTimelineStatus.Approved,
          InvoiceTimelineStatus.Paid,
          InvoiceTimelineStatus.Deposited
        ];
        if (isDualStage) {
          timelineStatuses = [
            InvoiceTimelineStatus.Open,
            InvoiceTimelineStatus.PreApproved,
            InvoiceTimelineStatus.Approved,
            InvoiceTimelineStatus.Paid,
            InvoiceTimelineStatus.Deposited
          ];
        }
        if (isRejected) {
          timelineStatuses = [
            InvoiceTimelineStatus.Open,
            InvoiceTimelineStatus.Rejected
          ];
        }

        function moveToPayeeDetails() {
          history.push(`/member/payees/${payable.payeeId}`);
        }

        return (
          <WSList gap="XL">
            <WSElement>
              <WSFlexBox.CenterY>
                {icon && <WSIcon block name={icon} color={iconColor} mr="M" />}
                <WSText.Heading5>{header}</WSText.Heading5>
              </WSFlexBox.CenterY>
              <WSText>{subheader}</WSText>
            </WSElement>

            {payee && (
              <WSFlexBox gap="S">
                <WSButton.Link onClick={moveToPayeeDetails}>
                  {selectorPayeeDisplayName(payee)}
                </WSButton.Link>
                {payee.email && (
                  <WSButton.Link onClick={moveToPayeeDetails}>
                    {payee.email}
                  </WSButton.Link>
                )}
              </WSFlexBox>
            )}

            <InvoiceStatusTimeline
              currentStatus={timelineStatus}
              statuses={timelineStatuses}
            />

            {(!!buttonProps || menuItems.length > 0) && (
              <WSFlexBox
                wrap="nowrap"
                alignItems="center"
                fullWidth
                justify="flex-end"
              >
                {buttonProps ? (
                  <WSButton
                    fullWidth
                    {...buttonProps}
                    mr={menuItems.length > 0 ? "M" : undefined}
                  />
                ) : "onClick" in menuItems[0] ? (
                  <WSButton
                    fullWidth
                    {...(menuItems[0] as WSButtonProps)}
                    kind="Secondary"
                    mr={menuItems.length > 0 ? "M" : undefined}
                  />
                ) : null}
                {updateWorkflowStatusMeta.isLoading ? (
                  <WSLoader.Spinner size="S" />
                ) : (
                  menuItems.length > 0 && (
                    <Actions items={menuItems} name="payableActions" />
                  )
                )}
              </WSFlexBox>
            )}
          </WSList>
        );
      })()}
    </WSElement>
  );
};
